/**
 * Chip — React (web) implementation.
 *
 * All styling references the shared variant maps in Chip.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import type { IconName } from '../../elements/icons/registry';
import { spacing } from '../../elements/spacing';
import { colors } from '../../elements/colors';
import { states } from '../../elements/states';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import type { ChipVariant, ChipSize, ChipIconPosition } from './Chip';
import {
  chipColorsFor,
  typographyForChipSize,
  paddingForChipSize,
  iconSizeForChipSize,
  CHIP_BORDER_RADIUS,
  CHIP_L_HEIGHT,
  CHIP_ICON_GAP,
} from './Chip';

// Web-only pointer and validation looks; native chips have neither.
const CHIP_HOVER_BACKGROUND = colors.background.secondary;
const CHIP_HOVER_BORDER = colors.stroke.primary;
const CHIP_INVALID_BORDER = colors.button.critical;
const FOCUS_STYLE_ID = 'ds-chip-focus';

export interface WebChipProps {
  actionId?: string;
  closeActionId?: string;
  label: string;
  variant?: ChipVariant;
  size?: ChipSize;
  selected?: boolean;
  disabled?: boolean;
  closable?: boolean;
  /**
   * Shows the error look (the border, or an inset ring on a borderless chip)
   * and marks an interactive chip invalid. Web only; a static chip carries no
   * ARIA signal.
   */
  invalid?: boolean;
  iconName?: IconName;
  iconPosition?: ChipIconPosition;
  onPress?: () => void;
  onClose?: () => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Chip({
  actionId = 'ui.chip.activate',
  closeActionId = 'ui.chip.remove',
  label,
  variant = 'Default',
  size = 'M',
  selected = false,
  disabled = false,
  closable = false,
  invalid = false,
  iconName,
  iconPosition = 'leading',
  onPress,
  onClose,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebChipProps) {
  const [pointerOver, setPointerOver] = React.useState(false);
  React.useEffect(() => {
    if (onPress) ensureFocusVisibleStyle(FOCUS_STYLE_ID, ['[data-ds-chip]']);
  }, [onPress]);
  const chipColors = chipColorsFor(variant, selected, disabled);
  const typo = typographyForChipSize[size];
  const pad = paddingForChipSize[size];
  const iconSize = iconSizeForChipSize[size];

  const hasBorder = variant === 'Outline' || (!selected && variant === 'Default');
  const hovered =
    pointerOver && Boolean(onPress) && !disabled && !selected && variant === 'Default';
  const borderColor = invalid
    ? CHIP_INVALID_BORDER
    : hovered
      ? CHIP_HOVER_BORDER
      : chipColors.border;

  const style: React.CSSProperties = {
    display: 'inline-flex',
    boxSizing: 'border-box',
    alignItems: 'center',
    flexDirection: 'row',
    paddingTop: pad.vertical,
    paddingBottom: pad.vertical,
    paddingLeft: pad.horizontal,
    paddingRight: pad.horizontal,
    borderRadius: CHIP_BORDER_RADIUS,
    backgroundColor: hovered ? CHIP_HOVER_BACKGROUND : chipColors.background,
    border: hasBorder ? `${states.borderWidth.default}px solid ${borderColor}` : 'none',
    // A borderless chip shows its error as an inset ring, so it keeps its size.
    boxShadow:
      invalid && !hasBorder
        ? `inset 0 0 0 ${states.borderWidth.default}px ${CHIP_INVALID_BORDER}`
        : undefined,
    fontFamily: typo.fontFamily,
    fontWeight: typo.fontWeight,
    fontSize: typo.fontSize,
    lineHeight: `${typo.lineHeight}px`,
    letterSpacing: typo.letterSpacing,
    color: chipColors.text,
    cursor: disabled ? 'not-allowed' : onPress ? 'pointer' : 'default',
    userSelect: 'none',
    minHeight: size === 'L' ? CHIP_L_HEIGHT : undefined,
  };

  // A flex box, so the inline SVG centres on the label line instead of
  // sitting on the text baseline a little above it.
  const iconStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    ...(iconPosition === 'leading'
      ? { marginRight: CHIP_ICON_GAP }
      : { marginLeft: CHIP_ICON_GAP }),
    fontSize: iconSize,
  };

  const closeStyle: React.CSSProperties = {
    marginLeft: CHIP_ICON_GAP,
    fontSize: iconSize,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    color: chipColors.text,
    lineHeight: 1,
  };

  const content = (
    <>
      {iconName && iconPosition === 'leading' ? (
        <span style={iconStyle} aria-hidden>
          <Icon name={iconName} size={iconSize} color={chipColors.text} aria-hidden />
        </span>
      ) : null}
      <span>{label}</span>
      {iconName && iconPosition === 'trailing' ? (
        <span style={iconStyle} aria-hidden>
          <Icon name={iconName} size={iconSize} color={chipColors.text} aria-hidden />
        </span>
      ) : null}
      {closable && onClose && (
        <button
          data-analytics-action={closeActionId}
          type="button"
          style={closeStyle}
          disabled={disabled}
          onClick={
            disabled
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  onClose();
                }
          }
          aria-label={`Remove ${label}`}
        >
          ✕
        </button>
      )}
    </>
  );

  if (onPress) {
    return (
      <button
        data-analytics-action={actionId}
        type="button"
        style={style}
        disabled={disabled}
        onClick={disabled ? undefined : onPress}
        {...mouseHoverHandlers(setPointerOver)}
        aria-pressed={selected}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel ?? label}
        data-testid={testId}
        data-ds-chip
      >
        {content}
      </button>
    );
  }

  // A plain `<span>` already carries the intended static, non-interactive text
  // semantics. Deliberately no `role` and no `aria-label`: RN's native
  // `accessibilityRole="text"` (Chip.native.tsx) has NO ARIA equivalent — the
  // web platform has no `text` role, so browsers ignore it — and `aria-label`
  // is prohibited on the resulting `generic` role (ARIA 1.2). The accessible
  // name therefore comes from the visible label, with the decorative icons
  // already `aria-hidden` above, which is the correct name for static text.
  return (
    <span style={style} data-testid={testId}>
      {content}
    </span>
  );
}

export interface WebChipGroupProps {
  options: { label: string; value: string; iconName?: IconName }[];
  selected: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  size?: ChipSize;
}

export function ChipGroup({
  options,
  selected,
  onChange,
  multiple = false,
  size = 'M',
}: WebChipGroupProps) {
  const isSelected = (value: string): boolean => {
    if (Array.isArray(selected)) return selected.includes(value);
    return selected === value;
  };

  const handlePress = (value: string) => {
    if (multiple && Array.isArray(selected)) {
      if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    } else {
      onChange(value);
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.s,
  };

  return (
    <div style={containerStyle}>
      {options.map((option) => (
        <Chip
          actionId="ui.chip-group.change"
          key={option.value}
          label={option.label}
          iconName={option.iconName}
          selected={isSelected(option.value)}
          onPress={() => handlePress(option.value)}
          size={size}
          variant="Default"
          disabled={false}
          closable={false}
        />
      ))}
    </div>
  );
}
